import { useTranslations } from '@vocab/react';
import {
  MenuItem,
  OverflowMenu,
  MenuItemLink,
  useToast,
  Box,
} from 'braid-design-system';

import { handleAuthCheck } from 'src/core/utils/authUtil';
import { useCommunityGuidelines } from 'src/core/utils/communityGuidelinesUils';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useLoginModal } from 'src/provider/loginModal';
import { useOptionDialog } from 'src/provider/optionDialog';
import { useProfile } from 'src/provider/profile';
import { useReplies } from 'src/provider/replies';

import DeleteDialog from '../../Dialogs/DeleteDialog/DeleteDialog';
import ReportDialog from '../../Dialogs/ReportDialog/ReportDialog';

import translations from './.vocab';

import * as styles from './ReplyOptionsButton.css';

export default () => {
  const { t } = useTranslations(translations);
  const showToast = useToast();

  const { trackAnalyticsInteraction } = useAnalytics();
  const { openLoginDialog } = useLoginModal();

  const { authStatus } = useAuthStatus();
  const {
    state: { author, isLoading: isProfileLoading },
  } = useProfile();
  const { replyDispatch } = useReplies();
  const {
    optionDialogState: { authorId, contentId, interaction, showDialog },
    optionDialogDispatch,
  } = useOptionDialog();

  const handleDialogClose = () =>
    optionDialogDispatch({ type: 'CLOSE_DIALOG' });
  const handleDialogOpen = () => {
    if (
      handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    ) {
      optionDialogDispatch({ type: 'SHOW_DIALOG' });
    }
  };

  const handleDeleteSubmit = () => replyDispatch({ type: 'IS_LOADING' });

  const handleDeleteSuccess = (message: string) => {
    if (interaction === 'reply') {
      replyDispatch({
        type: 'DELETE_REPLY',
        payload: { replyId: contentId },
      });
    }

    replyDispatch({ type: 'COMPLETE_LOADING' });
    showToast({ message, tone: 'positive' });
  };
  const handleDeleteError = (message: string) => {
    replyDispatch({ type: 'COMPLETE_LOADING' });
    showToast({ message, tone: 'critical' });
  };

  const handleReportSuccess = (
    linkAction: string,
    linkPosition: string,
    reportReason: string,
    message: string,
  ) => {
    trackAnalyticsInteraction('ReportClick', {
      ...(interaction === 'reply' && { replyId: contentId }),
      linkAction,
      linkPosition,
      reportReason,
    });
    showToast({ message, tone: 'positive' });
  };
  const handleReportError = (message: string) =>
    showToast({ message, tone: 'critical' });

  const isUnauthenticated = authStatus === 'unauthenticated';
  const isAuthor = authorId === author.id;

  // Need to wait for profile to load before retrieve the author.id
  const isShowDeleteButton = !isProfileLoading && isAuthor;
  const isShowReportButton =
    (!isProfileLoading && !isAuthor) || isUnauthenticated;

  const communityGuidelines = useCommunityGuidelines();
  return (
    <>
      <Box className={styles.replyOptionsButtonContainer}>
        <OverflowMenu label="Options">
          {isShowDeleteButton && (
            <MenuItem onClick={handleDialogOpen}>{t('deleteButton')}</MenuItem>
          )}

          {isShowReportButton && (
            <MenuItem onClick={handleDialogOpen}>{t('reportButton')}</MenuItem>
          )}

          <MenuItemLink
            id="reply-options-button-guidelines"
            href={communityGuidelines}
            target="_blank"
          >
            {t('guidelinesButton')}
          </MenuItemLink>
        </OverflowMenu>
      </Box>

      {isShowDeleteButton && (
        <DeleteDialog
          id="delete-dialog"
          contentId={contentId}
          interaction={interaction}
          open={showDialog}
          onClose={handleDialogClose}
          onDeleteSubmit={handleDeleteSubmit}
          onDeleteSuccess={handleDeleteSuccess}
          onDeleteError={handleDeleteError}
        />
      )}

      {isShowReportButton && (
        <ReportDialog
          id="report-dialog"
          contentId={contentId}
          interaction={interaction}
          open={showDialog}
          onClose={handleDialogClose}
          onReportSuccess={handleReportSuccess}
          onReportError={handleReportError}
        />
      )}
    </>
  );
};
